/**
 * checkout-intent v1.2 (2026-10-07, cc_tasks row 493, T533): a refused pack checkout says why (packs are
 * for Pro and higher) instead of "Edge Function returned a non-2xx status code".
 *
 * checkout-intent v1.1 (2026-09-29, cc_tasks row 251, T290): plan intents go through planUrl, so a
 * subscriber gets the plan switch, never a second subscription.
 *
 * checkout-intent — WHAT THE VISITOR CAME TO BUY, REMEMBERED ACROSS SIGNUP.
 *
 * ── THE RULE, AND WHERE IT WAS BROKEN ─────────────────────────────────────
 *
 * A logged-out visitor who clicks a plan or a credit pack must land on THAT
 * checkout after they register — not on a generic dashboard. That is locked.
 *
 * It was not happening, and not only for Enterprise. /pricing has linked Pro to
 * `/auth?plan=pro` and Agency to `/auth?plan=agency` all along, and Auth.tsx
 * read that param for ATTRIBUTION only — it writes `profiles.first_touch_plan`
 * and then routes to /onboarding or /dashboard like any other signup. Somebody
 * who clicked "Start Pro", entered an email and a password, and expected to pay
 * was shown a dashboard instead, with no mention of the thing they had just
 * chosen.
 *
 * ── WHY localStorage AND NOT ONLY THE URL ─────────────────────────────────
 *
 * Google OAuth leaves the site and comes back on a different URL, so a query
 * parameter does not survive it. The same reason first-touch attribution keeps
 * its own copy. The intent is written at the moment of the click, read once
 * after the session exists, and cleared — so it cannot fire twice or leak into
 * a later, unrelated signup.
 *
 * ── IT NEVER CARRIES A PRICE ──────────────────────────────────────────────
 *
 * Only a plan slug or a pack SKU. create-checkout resolves both server-side —
 * the pack from credit_pack_skus, the plan from its own table — so a tampered
 * localStorage value cannot buy anything at a price of its choosing.
 */

import { adsConversion, adsBeginCheckout } from '@/lib/adsConversion';
import { planUrl } from '@/lib/planSwitch';

const KEY = 'mf_checkout_intent';
/** Longer than a signup, shorter than a change of mind. */
const MAX_AGE_MS = 60 * 60 * 1000;

export type CheckoutIntent =
  | { kind: 'plan'; plan: string; at: number }
  | { kind: 'pack'; sku: string; at: number };

/** Record what the visitor clicked, before sending them to sign up. */
export function rememberCheckoutIntent(intent: { kind: 'plan'; plan: string } | { kind: 'pack'; sku: string }): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...intent, at: Date.now() }));
  } catch {
    // A private window with storage blocked. The URL parameter is the fallback
    // and the visitor still reaches signup — they just land on the dashboard.
  }
}

/**
 * Read the pending intent, ONCE. Reading clears it: a resumed checkout that is
 * cancelled must not re-fire the next time the user signs in.
 */
export function takeCheckoutIntent(search?: string): CheckoutIntent | null {
  let stored: CheckoutIntent | null = null;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw) as CheckoutIntent;
      if (p && typeof p.at === 'number' && Date.now() - p.at < MAX_AGE_MS) stored = p;
    }
    localStorage.removeItem(KEY);
  } catch { /* unreadable storage — fall through to the URL */ }

  if (stored) return stored;

  // The URL is the fallback for a first visit that never got to write storage.
  const params = new URLSearchParams(search ?? window.location.search);
  const pack = params.get('pack');
  if (pack) return { kind: 'pack', sku: pack.slice(0, 64), at: Date.now() };
  const plan = params.get('plan');
  // "free" is a real answer and is not a checkout.
  if (plan && plan !== 'free') return { kind: 'plan', plan: plan.slice(0, 64), at: Date.now() };
  return null;
}

export type ResumeResult = { url: string | null; error: string | null };

/**
 * Turn a remembered intent into a Stripe checkout URL.
 *
 * Returns the reason on failure rather than null-and-nothing: the caller has to
 * be able to tell the user why they are not looking at a payment page. A dead
 * click is the one outcome this must never produce.
 */
export async function resumeCheckout(
  // deno-lint-ignore no-explicit-any
  supabase: any,
  intent: CheckoutIntent,
): Promise<ResumeResult> {
  // A SKU or a plan slug, and never a price. create-checkout reads the amount
  // from credit_pack_skus or plan_definitions, so this cannot name its own
  // price — and there is no copy of a Stripe id here to go stale.
  // T290: a plan intent from somebody who already subscribes becomes the plan switch (their
  // subscription moved, prorated, back to /pricing), never a second subscription.
  if (intent.kind === 'plan') {
    try {
      const r = await planUrl({ plan: intent.plan, returnTo: '/pricing' });
      if (r.current) return { url: null, error: 'You are already on this plan.' };
      if (r.switched) return { url: r.url, error: r.url ? null : 'The plan switch could not be opened.' };
      if (!r.url) return { url: null, error: 'The checkout could not be started.' };
      await adsConversion('checkout_started', {});
      await adsBeginCheckout();
      return { url: r.url, error: null };
    } catch (e) {
      return { url: null, error: e instanceof Error ? e.message : 'The checkout could not be started.' };
    }
  }
  const body = { bundle: 'credit_pack', sku: intent.sku, returnTo: '/dashboard/settings?tab=billing' };

  const { data, error } = await supabase.functions.invoke('create-checkout', { body });
  if (error) {
    // T533: the refusal carries its own sentence (403 pack_requires_pro: "Credit packs are for Pro and
    // higher. See the plans at ..."). Reading only error.message showed "Edge Function returned a
    // non-2xx status code" to a visitor who had just signed up to buy a pack.
    let msg = error.message || 'The checkout could not be started.';
    try {
      const b = await (error as { context?: { json?: () => Promise<{ error?: string }> } })?.context?.json?.();
      if (b?.error) msg = String(b.error);
    } catch { /* keep the generic one */ }
    return { url: null, error: msg };
  }
  if (!data?.url) return { url: null, error: 'The checkout could not be started.' };
  // Google Ads checkout_started (cc_tasks row 88). Awaited (max ~1.5 s) because the
  // caller redirects to the checkout right after this returns.
  await adsConversion('checkout_started', { transaction_id: data?.sessionId ? String(data.sessionId) : undefined });
  // Google Ads Begin checkout (T258): /auth redirects to Stripe as soon as this returns.
  await adsBeginCheckout();
  return { url: String(data.url), error: null };
}
